import { Box, ButtonBase, Paper, Typography, useTheme } from "@mui/material";
import { SECTIONS_META } from "./SectionsMeta";
import { useReportData } from "./UseReportData";
import { SectionFrame, StatusDot, StatusKey, STATUS, fmt, fmtPct, fmtT, statusOf, statusPhrase, timeMetrics, varPct } from "./Reportkit";

/** Headline figure + status per section, from the /compass response. */
function buildFacts(d) {
  const p = d.production, f = timeMetrics(d.fleet), fp = timeMetrics(d.fleet, true);
  const ev = d.events, evP = d.previousEvents;
  const lpt = d.fuel.tonnes ? d.fuel.truckLitres / d.fuel.tonnes : null;
  const lptP = d.previousFuel.tonnes ? d.previousFuel.truckLitres / d.previousFuel.tonnes : null;
  const head = p.auT ? p.auG / p.auT : null, headP = p.planAuT ? p.planAuG / p.planAuT : null;
  return {
    production: { value: fmtT(p.actual), label: `${fmtPct(varPct(p.actual, p.plan), 1, true)} vs plan`, status: statusOf(p.actual, p.plan), basis: "plan" },
    flow: { value: fmtT(p.oreActual), label: "ore · routes & flow", status: statusOf(p.oreActual, p.orePlan), basis: "plan" },
    grades: { value: head == null ? "—" : `${fmt(head, 2)} g/t`, label: `head grade · plan ${fmt(headP, 2)}`, status: statusOf(head, headP), basis: "plan" },
    equipment: { value: f.pa == null ? "—" : `${fmt(f.pa, 1)}%`, label: `PA · plan ${fmt(fp.pa, 1)}%`, status: f.cal ? statusOf(f.pa, fp.pa) : "none", basis: "plan" },
    safety: { value: `${fmt(ev.unplannedHours, 0)} h`, label: `unplanned downtime · ${fmt(ev.unplannedCount)} events`, status: statusOf(ev.unplannedHours, evP.unplannedHours, "lower"), basis: "prev" },
    fuel: { value: lpt == null ? "—" : `${fmt(lpt, 3)} L/t`, label: `${fmt(d.fuel.litres / 1000, 0)} kL dispensed`, status: statusOf(lpt, lptP, "lower"), basis: "prev" },
    voice: { value: fmt(d.voiceCount), label: "comments in period", status: "none", basis: "plan" },
    pa: f.pa, paP: fp.pa, head, headP,
  };
}

function Compass({ d, facts, onNavigate }) {
  const t = useTheme();
  const p = d.production;
  const paper = t.palette.background.paper, line = t.palette.divider, ink = t.palette.text.primary, muted = t.palette.text.secondary;
  const brand = t.palette.mode === "dark" ? "#3FA37A" : "#0B5D3B";
  const col = (s) => (s === "none" ? "#C9A227" : STATUS[s].color);

  const top = [
    { id: "plan", x: 24, y: 52, w: 220, h: 124, title: "Mine Plan", sub: "Planned tonnes", value: fmtT(p.plan), label: "Planned TMM", status: "none", go: "production" },
    { id: "prod", x: 282, y: 52, w: 220, h: 124, title: "Production", sub: "Dispatch tonnage vs plan", value: fmtT(p.actual), label: facts.production.label, status: facts.production.status, go: "production" },
    { id: "fleet", x: 540, y: 52, w: 220, h: 124, title: d.fleetFamily === "HAUL TRUCK" ? "Haul Fleet" : "Fleet", sub: "Physical availability", value: facts.equipment.value, label: facts.equipment.label, status: facts.equipment.status, go: "equipment" },
    { id: "ore", x: 830, y: 34, w: 340, h: 80, compact: true, title: "Ore", sub: "HG + LG", value: fmtT(p.oreActual), label: `${fmtPct(varPct(p.oreActual, p.orePlan), 1, true)} vs plan`, status: statusOf(p.oreActual, p.orePlan), go: "production" },
    { id: "waste", x: 830, y: 130, w: 340, h: 80, compact: true, title: "Waste", sub: "OB + NAG + PAG", value: fmtT(p.wasteActual), label: `${fmtPct(varPct(p.wasteActual, p.wastePlan), 1, true)} vs plan`, status: statusOf(p.wasteActual, p.wastePlan), go: "production" },
  ];
  const bottom = [
    { id: "flow", title: "Flow & Routes", sub: "Sources → destinations", go: "flow", ...facts.flow },
    { id: "grades", title: "Grades & Quality", sub: "Au · Cu", go: "grades", ...facts.grades },
    { id: "safety", title: "Safety & Events", sub: "Breakdowns · NPT · FLHA", go: "safety", ...facts.safety },
    { id: "fuel", title: "Fuel", sub: "Dispensing & intensity", go: "fuel", ...facts.fuel },
    { id: "voice", title: "Operator Voice", sub: "Comments from the field", go: "voice", ...facts.voice },
    { id: "crew", title: "Crew & Operators", sub: "Operator ranking", value: "Soon", label: "needs operator data", status: "none", soon: true },
  ].map((n, i) => ({ ...n, x: 24 + i * 194, y: 292, w: 184, h: 124 }));
  const nodes = [...top, ...bottom];
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));

  const edge = (a, b) => {
    const A = byId[a], B = byId[b], x1 = A.x + A.w, y1 = A.y + A.h / 2, x2 = B.x, y2 = B.y + B.h / 2, mx = (x1 + x2) / 2;
    return <path key={a + b} className="cflow" d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2 - 6},${y2}`} fill="none" stroke={brand} strokeWidth={2.4} strokeDasharray="7 7" opacity={0.7} markerEnd="url(#arr)" />;
  };
  const up = (a, b) => {
    const A = byId[a], B = byId[b], x1 = A.x + A.w / 2, y1 = A.y, x2 = B.x + B.w / 2, y2 = B.y + B.h, my = (y1 + y2) / 2;
    return <path key={a + b} className="cflow" d={`M${x1},${y1} C${x1},${my} ${x2},${my} ${x2},${y2 + 4}`} fill="none" stroke="#C9A227" strokeWidth={1.6} strokeDasharray="3 6" opacity={0.8} />;
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, overflow: "hidden" }}>
      <style>{`@keyframes cflowAnim{to{stroke-dashoffset:-28}} .cflow{animation:cflowAnim 1.6s linear infinite} @media (prefers-reduced-motion:reduce){.cflow{animation:none}} .cnode:hover rect.bg{stroke:#C9A227;stroke-width:2} .cnode:focus{outline:none}`}</style>
      <svg viewBox="0 0 1200 460" width="100%" role="group" aria-label="MineProd Compass report map">
        <defs><marker id="arr" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill={brand} /></marker></defs>
        <rect x="6" y="8" width="1188" height="212" rx="14" fill="none" stroke={line} strokeDasharray="4 4" />
        <text x="22" y="32" fontSize="10.5" letterSpacing="1.2" fontWeight="700" fill={muted}>VALUE CHAIN · PLAN TO OUTPUT</text>
        <rect x="6" y="252" width="1188" height="190" rx="14" fill="none" stroke={line} strokeDasharray="4 4" />
        <text x="22" y="276" fontSize="10.5" letterSpacing="1.2" fontWeight="700" fill={muted}>INSIGHT · QUALITY · SAFETY · RESOURCES</text>
        {edge("plan", "prod")}{edge("prod", "fleet")}{edge("fleet", "ore")}{edge("fleet", "waste")}
        {up("safety", "fleet")}{up("fuel", "fleet")}{up("grades", "ore")}{up("flow", "prod")}
        {nodes.map((n) => {
          const go = () => !n.soon && onNavigate(n.go);
          const c = n.compact;
          return (
            <g key={n.id} className="cnode" transform={`translate(${n.x},${n.y})`} tabIndex={n.soon ? -1 : 0} role={n.soon ? undefined : "link"}
              aria-label={`${n.title}: ${n.value} ${n.label}`} onClick={go} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && go()}
              style={{ cursor: n.soon ? "default" : "pointer", opacity: n.soon ? 0.5 : 1 }}>
              <rect className="bg" width={n.w} height={n.h} rx="12" fill={paper} stroke={line} strokeWidth="1.2" strokeDasharray={n.soon ? "5 4" : undefined} />
              <rect width="4" height={n.h} rx="2" fill={col(n.status)} />
              <text x="16" y="26" fontSize="15" fontWeight="700" fill={ink}>{n.title}</text>
              <text x="16" y="44" fontSize="11.5" fill={muted}>{n.sub}</text>
              <text x="16" y={c ? 68 : 84} fontSize="20" fontWeight="700" fill={ink}>{n.value}</text>
              <text x={c ? 16 + Math.min(170, 14 + String(n.value).length * 12) : 16} y={c ? 68 : 104} fontSize="11" fill={muted}>{n.label}</text>
              {n.status !== "none" && (
                <g transform={`translate(${n.w - 24},24)`}>
                  <circle r="9" fill={STATUS[n.status].color} />
                  <text textAnchor="middle" y="3.5" fontSize="10" fontWeight="700" fill={STATUS[n.status].ink}>{STATUS[n.status].icon}</text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </Paper>
  );
}

export default function OverviewSection({ req, onNavigate }) {
  const st = useReportData("compass", req);
  const d = st.data;
  const facts = d ? buildFacts(d) : null;

  return (
    <SectionFrame eyebrow="Start here" title="MineProd Compass" desc="How every report connects — click any node or tile to open it" state={st}>
      {d && (
        <>
          <Compass d={d} facts={facts} onNavigate={onNavigate} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>All report sections</Typography>
            <StatusKey />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 2 }}>
            {SECTIONS_META.filter((s) => s.id !== "overview").map((s) => {
              const Icon = s.icon;
              const fact = facts[s.id];
              return (
                <ButtonBase key={s.id} disabled={s.soon} onClick={() => onNavigate(s.id)}
                  sx={{ display: "flex", alignItems: "flex-start", justifyContent: "flex-start", textAlign: "left", gap: 1.5, p: 1.75, border: 1, borderColor: "divider", borderRadius: 2, bgcolor: "background.paper", opacity: s.soon ? 0.55 : 1, "&:hover": { borderColor: "warning.main" } }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: 1.5, bgcolor: "action.selected", display: "grid", placeItems: "center", flexShrink: 0 }}><Icon fontSize="small" /></Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography fontWeight={700} variant="body2">{s.title}</Typography>
                      {fact && fact.status !== "none" && <StatusDot status={fact.status} size={14} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary" component="div">{s.desc}</Typography>
                    {fact ? (
                      <Box sx={{ mt: 0.75 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>{fact.value}</Typography>
                        <Typography variant="caption" color="text.secondary" component="div">{fact.label}</Typography>
                        <Typography variant="caption" sx={{ color: fact.status === "none" ? "text.secondary" : STATUS[fact.status].color === "#FAB219" ? "warning.dark" : STATUS[fact.status].color, fontWeight: 600 }}>
                          {statusPhrase(fact.status, fact.basis)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>Coming next</Typography>
                    )}
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
        </>
      )}
    </SectionFrame>
  );
}